import Dexie from 'dexie';
import type { Table } from 'dexie';
import { MAX_REFERENCE_BYTES, MimoError, TRANSFER_FAILURE } from './contracts';
import type { AudioHandle, TtsMode } from './contracts';

export interface LocalAudio extends AudioHandle {
  userId: string; key: string; blob: Blob; links: string[]; preview: boolean; createdAt: number;
}
export interface LocalReference { userId: string; blob: Blob; name: string; sha256: string; consent: true; }
export interface PendingAck extends AudioHandle { userId: string; key: string; }
export interface LocalMode { scope: string; mode: TtsMode; }

/** Durable per-account TTS snapshot used by the encrypted backup (ACK queue excluded). */
export interface MimoSnapshot {
  audio: LocalAudio[];
  reference?: LocalReference;
  modes: LocalMode[];
}
export class MimoDatabase extends Dexie {
  audio!: Table<LocalAudio, [string, string]>;
  references!: Table<LocalReference, string>;
  pendingAcks!: Table<PendingAck, [string, string]>;
  modes!: Table<LocalMode, string>;
  constructor(name = 'zhixin-mimo-device-v1') {
    super(name);
    this.version(1).stores({ audio: '[userId+key], userId', references: 'userId', pendingAcks: '[userId+id], userId', modes: 'scope' });
  }
}
export async function blobBytes(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result: unknown = reader.result;
      if (result !== null && typeof result === 'object' && 'byteLength' in result) {
        resolve(result as ArrayBuffer);
      } else {
        reject(new MimoError(TRANSFER_FAILURE));
      }
    };
    reader.onerror = () => reject(new MimoError(TRANSFER_FAILURE));
    reader.readAsArrayBuffer(blob);
  });
}
export async function digest(value: Blob | string): Promise<string> {
  const buffer = typeof value === 'string' ? new TextEncoder().encode(value) : await blobBytes(value);
  const hash = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(hash), (byte) => byte.toString(16).padStart(2, '0')).join('');
}
export async function verifyAudio(blob: Blob, handle: Pick<AudioHandle, 'bytes' | 'sha256'>): Promise<void> {
  if (blob.size !== handle.bytes || await digest(blob) !== handle.sha256) throw new MimoError(TRANSFER_FAILURE);
}

export async function createLocalReference(userId: string, file: File, consent: boolean, maxBytes = MAX_REFERENCE_BYTES): Promise<LocalReference> {
  if (!userId) throw new MimoError('请先登录');
  if (!consent) throw new MimoError('请确认已获得参考音色授权');
  const mime = /\.wav$/i.test(file.name) ? 'audio/wav' : /\.mp3$/i.test(file.name) ? 'audio/mpeg' : '';
  if (!file.size || file.size > Math.min(maxBytes, MAX_REFERENCE_BYTES) || !mime) throw new MimoError('参考音频必须为有效的 WAV 或 MP3，且不超过上传限制');
  const bytes = new Uint8Array(await blobBytes(file));
  const wav = bytes.length >= 12 && String.fromCharCode(...bytes.slice(0, 4)) === 'RIFF' && String.fromCharCode(...bytes.slice(8, 12)) === 'WAVE';
  const mp3 = String.fromCharCode(...bytes.slice(0, 3)) === 'ID3' || (bytes[0] === 0xff && ((bytes[1] ?? 0) & 0xe0) === 0xe0);
  if (mime === 'audio/wav' ? !wav : !mp3) throw new MimoError('参考音频必须为有效的 WAV 或 MP3，且不超过上传限制');
  const blob = new Blob([bytes], { type: mime });
  return { userId, blob, name: file.name, sha256: await digest(blob), consent: true };
}

export class MimoStorage {
  constructor(readonly db = new MimoDatabase()) {}
  async reference(userId: string): Promise<LocalReference | undefined> { return this.db.references.get(userId); }
  async saveReference(userId: string, file: File, consent: boolean, maxBytes = MAX_REFERENCE_BYTES): Promise<LocalReference> {
    const reference = await createLocalReference(userId, file, consent, maxBytes);
    await this.db.references.put(reference);
    const saved = await this.reference(userId);
    if (!saved) throw new MimoError('参考音频未能保存在此设备');
    await verifyAudio(saved.blob, { bytes: reference.blob.size, sha256: reference.sha256 });
    return saved;
  }
  async removeReference(userId: string): Promise<void> { await this.db.references.delete(userId); }
  async readAudio(userId: string, key: string): Promise<LocalAudio | undefined> {
    const audio = await this.db.audio.get([userId, key]);
    if (audio) await verifyAudio(audio.blob, audio);
    return audio;
  }
  async saveAudio(audio: LocalAudio): Promise<LocalAudio> {
    await verifyAudio(audio.blob, audio);
    // The retry handle and blob commit together, so a lost ACK never requires synthesis again.
    await this.db.transaction('rw', this.db.audio, this.db.pendingAcks, async () => {
      const existing = await this.db.audio.get([audio.userId, audio.key]);
      await this.db.audio.put({ ...audio, links: [...new Set([...(existing?.links ?? []), ...audio.links])] });
      await this.db.pendingAcks.put({ userId: audio.userId, key: audio.key, id: audio.id, sha256: audio.sha256, bytes: audio.bytes, mime: audio.mime });
    });
    const saved = await this.readAudio(audio.userId, audio.key);
    if (!saved) throw new MimoError('音频未能保存在此设备');
    return saved;
  }
  async linkAudio(userId: string, key: string, messageId: string): Promise<void> {
    await this.db.transaction('rw', this.db.audio, async () => {
      const audio = await this.db.audio.get([userId, key]);
      if (audio && !audio.links.includes(messageId)) {
        await this.db.audio.put({ ...audio, links: [...audio.links, messageId] });
      }
    });
  }
  async unlinkMessage(userId: string, messageId: string): Promise<void> {
    await this.db.transaction('rw', this.db.audio, async () => {
      const audio = (await this.db.audio.where('userId').equals(userId).toArray()).filter((entry) => entry.links.includes(messageId));
      await this.db.audio.bulkPut(audio.map((entry) => ({ ...entry, links: entry.links.filter((link) => link !== messageId) })));
    });
  }
  async cleanup(userId: string, obsoleteBefore = Date.now() - 86400000, existingMessageIds?: ReadonlySet<string>): Promise<number> {
    return this.db.transaction('rw', this.db.audio, this.db.pendingAcks, async () => {
      const pending = new Set((await this.db.pendingAcks.where('userId').equals(userId).toArray()).map((ack) => ack.key));
      const owned = await this.db.audio.where('userId').equals(userId).toArray();
      if (existingMessageIds) {
        for (const audio of owned) {
          audio.links = audio.links.filter((link) => existingMessageIds.has(link));
          await this.db.audio.put(audio);
        }
      }
      const obsolete = owned
        .filter((audio) => audio.links.length === 0 && !pending.has(audio.key) && (audio.preview || audio.createdAt < obsoleteBefore));
      await this.db.audio.bulkDelete(obsolete.map((audio) => [userId, audio.key]));
      return obsolete.length;
    });
  }

  /** Read the durable TTS snapshot for one account. Pending ACK rows are never exported. */
  async exportSnapshot(userId: string): Promise<MimoSnapshot> {
    const [audio, reference, modes] = await Promise.all([
      this.db.audio.where('userId').equals(userId).toArray(),
      this.db.references.get(userId),
      this.userModes(userId),
    ]);
    return { audio, ...(reference === undefined ? {} : { reference }), modes };
  }

  /**
   * Write a backup snapshot back for one account.
   * Merge keeps the local ACK queue and unions message links; replace clears the account's
   * local TTS rows first. Pending ACK rows are intentionally left untouched (or cleared only
   * on replace, because they are not part of the backup).
   */
  async restoreSnapshot(
    userId: string,
    snapshot: MimoSnapshot,
    mode: 'merge' | 'replace' = 'merge',
  ): Promise<void> {
    await this.db.transaction(
      'rw',
      this.db.audio,
      this.db.references,
      this.db.modes,
      this.db.pendingAcks,
      async () => {
        if (mode === 'replace') {
          const [ownedAudio, modes, acks] = await Promise.all([
            this.db.audio.where('userId').equals(userId).toArray(),
            this.userModes(userId),
            this.db.pendingAcks.where('userId').equals(userId).toArray(),
          ]);
          await this.db.audio.bulkDelete(ownedAudio.map((audio) => [userId, audio.key]));
          await this.db.pendingAcks.bulkDelete(acks.map((ack) => [userId, ack.id]));
          await this.db.modes.bulkDelete(modes.map((entry) => entry.scope));
          await this.db.references.delete(userId);
        }
        for (const audio of snapshot.audio) {
          const existing = await this.db.audio.get([userId, audio.key]);
          const links = [...new Set([...(existing?.links ?? []), ...audio.links])];
          await this.db.audio.put({ ...audio, userId, links });
        }
        if (snapshot.reference !== undefined) {
          await this.db.references.put({ ...snapshot.reference, userId });
        }
        for (const entry of snapshot.modes) await this.db.modes.put(entry);
      },
    );
  }

  /** Delete one account's durable TTS data (used by full replacement before rebuilding). */
  async clearUserData(userId: string): Promise<void> {
    await this.restoreSnapshot(userId, { audio: [], modes: [] }, 'replace');
  }

  private async userModes(userId: string): Promise<LocalMode[]> {
    const all = await this.db.modes.toArray();
    return all.filter((entry) => {
      try {
        const parsed: unknown = JSON.parse(entry.scope);
        return Array.isArray(parsed) && parsed[0] === userId;
      } catch {
        return false;
      }
    });
  }

  async readMode(userId: string, conversationId?: string): Promise<TtsMode> {
    return (await this.db.modes.get(JSON.stringify([userId, conversationId ?? null])))?.mode ?? 'off';
  }
  async saveMode(userId: string, conversationId: string | undefined, mode: TtsMode): Promise<void> {
    await this.db.modes.put({ scope: JSON.stringify([userId, conversationId ?? null]), mode });
  }
}
export const mimoStorage = new MimoStorage();
